import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../../services/apiService';

export interface Notification {
  id: number;
  title: string;
  message: string;
  type: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS';
  status: 'UNREAD' | 'READ' | 'DELIVERED';
  createdAt: string;
  senderName?: string;
  isRead: boolean;
  isDelivered: boolean;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
};

// Async thunks
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get('/api/notifications');
      console.log('fetchNotifications - apiService response:', response);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch notifications');
    }
  }
);

export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.get('/api/notifications/unread/count');
      console.log('fetchUnreadCount - apiService response:', response);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch unread count');
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId: number, { rejectWithValue }) => {
    try {
      const response = await apiService.put(`/api/notifications/${notificationId}/read`);
      return { notificationId, response: response.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark notification as read');
    }
  }
);

export const markNotificationAsDelivered = createAsyncThunk(
  'notifications/markAsDelivered',
  async (notificationId: number, { rejectWithValue }) => {
    try {
      const response = await apiService.put(`/api/notifications/${notificationId}/delivered`);
      return { notificationId, response: response.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark notification as delivered');
    }
  }
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.notifications.unshift(action.payload);
      if (!action.payload.isRead) {
        state.unreadCount += 1;
      }
    },
    updateNotification: (state, action: PayloadAction<{ id: number; updates: Partial<Notification> }>) => {
      const index = state.notifications.findIndex(n => n.id === action.payload.id);
      if (index !== -1) {
        const notification = state.notifications[index];
        const wasUnread = !notification.isRead;
        const isNowRead = action.payload.updates.isRead === true;
        
        state.notifications[index] = { ...notification, ...action.payload.updates };
        
        // Update unread count
        if (wasUnread && isNowRead) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        } else if (!wasUnread && !isNowRead) {
          state.unreadCount += 1;
        }
      }
    },
    removeNotification: (state, action: PayloadAction<number>) => {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (notification && !notification.isRead) {
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
    },
    setUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchNotifications
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        console.log('fetchNotifications.fulfilled - action.payload:', action.payload);
        
        if (action.payload && action.payload.success && action.payload.data) {
          console.log('Processing notifications with success status');
          // Map backend NotificationMessageDTO to frontend Notification format
          const notifications = action.payload.data.map((dto: any) => {
            console.log('Processing DTO:', dto);
            const mapped = {
              id: dto.id,
              title: dto.notificationType || 'Notification',
              message: dto.message,
              type: dto.notificationType === 'SUCCESS' ? 'SUCCESS' : 
                    dto.notificationType === 'WARNING' ? 'WARNING' : 
                    dto.notificationType === 'ERROR' ? 'ERROR' : 'INFO',
              status: dto.messageStatus,
              createdAt: dto.createdDate,
              senderName: dto.senderName,
              isRead: dto.messageStatus === 'READ',
              isDelivered: dto.messageStatus === 'DELIVERED' || dto.messageStatus === 'READ',
            };
            console.log('Mapped notification:', mapped);
            return mapped;
          });
          console.log('Mapped notifications:', notifications);
          state.notifications = notifications;
          state.unreadCount = notifications.filter((n: Notification) => !n.isRead).length;
        } else if (action.payload && action.payload.data) {
          console.log('Processing notifications without success status');
          // Handle case where status might not be 'success' but data exists
          const notifications = action.payload.data.map((dto: any) => {
            console.log('Processing DTO (no success):', dto);
            const mapped = {
              id: dto.id,
              title: dto.notificationType || 'Notification',
              message: dto.message,
              type: dto.notificationType === 'SUCCESS' ? 'SUCCESS' : 
                    dto.notificationType === 'WARNING' ? 'WARNING' : 
                    dto.notificationType === 'ERROR' ? 'ERROR' : 'INFO',
              status: dto.messageStatus,
              createdAt: dto.createdDate,
              senderName: dto.senderName,
              isRead: dto.messageStatus === 'READ',
              isDelivered: dto.messageStatus === 'DELIVERED' || dto.messageStatus === 'READ',
            };
            console.log('Mapped notification (no success):', mapped);
            return mapped;
          });
          console.log('Mapped notifications:', notifications);
          state.notifications = notifications;
          state.unreadCount = notifications.filter((n: Notification) => !n.isRead).length;
        } else {
          // Handle case where payload is undefined or doesn't have expected structure
          console.warn('Unexpected notification response format:', action.payload);
          state.notifications = [];
          state.unreadCount = 0;
        }
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // fetchUnreadCount
    builder
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        console.log('fetchUnreadCount.fulfilled - action.payload:', action.payload);
        
        if (action.payload && action.payload.success && action.payload.data !== undefined) {
          console.log('Setting unread count to:', action.payload.data);
          state.unreadCount = action.payload.data;
        } else if (action.payload && action.payload.data !== undefined) {
          // Handle case where status might not be 'success' but data exists
          console.log('Setting unread count to (no success status):', action.payload.data);
          state.unreadCount = action.payload.data;
        } else {
          // Handle case where payload is undefined or doesn't have expected structure
          console.warn('Unexpected unread count response format:', action.payload);
          state.unreadCount = 0;
        }
      });

    // markNotificationAsRead
    builder
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const { notificationId } = action.payload;
        const notification = state.notifications.find(n => n.id === notificationId);
        if (notification && !notification.isRead) {
          notification.isRead = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      });

    // markNotificationAsDelivered
    builder
      .addCase(markNotificationAsDelivered.fulfilled, (state, action) => {
        const { notificationId } = action.payload;
        const notification = state.notifications.find(n => n.id === notificationId);
        if (notification) {
          notification.isDelivered = true;
        }
      });
  },
});

export const {
  addNotification,
  updateNotification,
  removeNotification,
  clearNotifications,
  setUnreadCount,
  clearError,
} = notificationSlice.actions;

export default notificationSlice.reducer; 